'use client'

import React, {
  type ReactNode,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  Building2,
  Calendar,
  CheckCircle2,
  Clock,
  DollarSign,
  ExternalLink,
  FileText,
  Files,
  ListFilter,
  Mail,
  Megaphone,
  Phone,
  Sparkles,
  Tag,
  User,
  Users,
} from 'lucide-react'
import { twMerge } from 'tailwind-merge'

import { ToggleBookmarkIconButton } from '@/components/bookmarks/ToggleBookmarkIconButton'
import { useCreateOpportunityChatSession } from '@/components/chat-v2/hooks/useChatSession'
import Button from '@/components/common/Button'
import { ResourcePermissionGate } from '@/components/common/PermissionGate'
import {
  PursuitActions,
  buildFederalGrantInput,
} from '@/components/opportunity/common/PursuitActionsV2'

import { cleanFileName } from '@/helpers/file'

import { useQuerySearchId } from '@/hooks/common/useQuerySearchId'
import { useRenderNowMs } from '@/hooks/common/useRenderNowMs'
import { useSafePush } from '@/hooks/navigation/useSafePush'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'

import type {
  FederalGrant,
  FederalGrantNotice,
} from '@/types/federal-grant/FederalGrant'
import { isUUID } from '@/types/Id'
import type { SearchOut } from '@/types/Search'

import {
  OpportunityDocumentsSection,
  OpportunityDocumentsTab,
  PageTitleV2,
  ShareIcon,
  SimilarOpportunitiesV2,
  ViewDocumentsButton,
} from '../../common'
import { BadgeV2 } from '../../common/BadgeV2'
import { CardV2 } from '../../common/CardV2'
import { ContentPanelV2 } from '../../common/ContentPanelV2'
import { DataBlockV2 } from '../../common/DataBlockV2'
import {
  type FederalGrantDiffs,
  useDaysRemaining,
  useFederalGrantDiffs,
  useNoticeScrollHighlight,
} from '../../common/hooks'
import { NoticesV2, useCardContext } from '../../common/NoticesV2'
import { SidebarWidgetV2 } from '../../common/SidebarWidgetV2'
import { TabPanelV2 } from '../../common/TabPanelV2'
import { formatCurrency, formatDate } from '../../common/transforms'
import { normalizeGrantData } from './transforms'

// ============================================================================
// Types
// ============================================================================

interface GrantDisplayV2Props {
  grant: FederalGrant
  similarOpportunities?: SearchOut[]
  isSimilarLoading?: boolean
  onShare?: () => void
}

type DocumentsTabEntrySource = 'default' | 'compact-file-click'

// ============================================================================
// GrantNoticeCard - Enhanced notice card content for NoticesV2
// ============================================================================

interface GrantNoticeCardProps {
  notice: FederalGrantNotice
  /** Previous notice for comparison (undefined for original notice) */
  previousNotice?: FederalGrantNotice
  /** Diff data for this notice (undefined for original notice) */
  diffs?: FederalGrantDiffs
  /** Whether this notice is an amendment */
  isAmendment: boolean
  /** When true, renders a compact version with fewer description lines */
  isCompact?: boolean
}

/**
 * Determines if a grant notice is an amendment based on position in timeline.
 * Notices are sorted newest-first, so the last notice is the original.
 */
function isAmendment(idx: number, totalNotices: number): boolean {
  // Single notice = original, not an amendment
  if (totalNotices === 1) return false
  // Last notice in descending-sorted array is the original
  return idx !== totalNotices - 1
}

/**
 * Pure helper to generate amendment title with optional description badge.
 * Extracted to avoid inline JSX in props.
 */
function getAmendmentTitle(
  isLatest: boolean,
  amendmentNum: number,
  descriptionChanged: boolean,
): ReactNode {
  const label = isLatest ? 'Latest Amendment' : `Amendment #${amendmentNum}`
  if (!descriptionChanged) return label
  return (
    <span>
      {label}
      <span className="ml-1.5 text-xs font-normal text-amber-600 dark:text-amber-500">
        · Description Updated
      </span>
    </span>
  )
}

/**
 * Format award range for display
 */
function formatAwardRange(notice: FederalGrantNotice): string | null {
  if (!notice.award_floor && !notice.award_ceiling) return null
  if (notice.award_floor && notice.award_ceiling) {
    return `${formatCurrency(parseFloat(notice.award_floor))} - ${formatCurrency(parseFloat(notice.award_ceiling))}`
  }
  if (notice.award_ceiling) {
    return `Up to ${formatCurrency(parseFloat(notice.award_ceiling))}`
  }
  if (notice.award_floor) {
    return `From ${formatCurrency(parseFloat(notice.award_floor))}`
  }
  return null
}

/**
 * GrantNoticeCard - Pure memoized component for notice card content
 * Follows rerender-memo pattern: extracts expensive rendering into memoized component
 */
const GrantNoticeCard = memo(function GrantNoticeCard({
  notice,
  previousNotice,
  diffs,
  isAmendment,
  isCompact = false,
}: GrantNoticeCardProps) {
  const { isExpanded } = useCardContext()
  const awardRange = formatAwardRange(notice)
  const hasDescription = Boolean(notice.description)

  // Show metadata when card is not compact OR when description is expanded
  const showMetadata = !isCompact || isExpanded

  return (
    <>
      {/* Header Section with Icon + Label */}
      <NoticesV2.Card.HeaderSection>
        <NoticesV2.Card.Icon />
        <NoticesV2.Card.Label />
        <NoticesV2.Card.HeaderContent>
          <NoticesV2.Card.Title>
            {notice.opportunity_title || 'Untitled Notice'}
          </NoticesV2.Card.Title>
          <NoticesV2.Card.Meta>
            {formatDate(notice.post_date)}
            {notice.status && ` · ${notice.status.toUpperCase()}`}
            {notice.version &&
              notice.version !== '0' &&
              ` · v${notice.version}`}
            {isAmendment && diffs?.descriptionChanged ? (
              <span className="ml-1.5 inline-flex -translate-y-px items-center rounded bg-amber-100 px-1.5 py-0.5 align-middle text-[10px] font-medium leading-none text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 sm:ml-2 sm:translate-y-0 sm:text-xs">
                Updated
              </span>
            ) : null}
          </NoticesV2.Card.Meta>
        </NoticesV2.Card.HeaderContent>
        <NoticesV2.Card.CurrentBadge />
      </NoticesV2.Card.HeaderSection>

      {/* Body Section */}
      <NoticesV2.Card.BodySection>
        {hasDescription ? (
          <NoticesV2.Card.Description
            markdown
            lines={isCompact ? 2 : 4}
            className={
              isCompact ? 'text-primary-500 dark:text-primary-400' : undefined
            }
          >
            {notice.description}
          </NoticesV2.Card.Description>
        ) : (
          <p className="text-sm italic text-primary-400 dark:text-primary-500">
            No description provided.
          </p>
        )}

        {/* Metadata Grid - show when not compact OR when expanded */}
        {showMetadata && (
          <NoticesV2.Card.MetadataGrid>
            <NoticesV2.Card.MetadataItem
              label="Close Date"
              value={
                diffs?.closeDateChanged && previousNotice?.close_date ? (
                  <span className="font-semibold text-amber-700 dark:text-amber-400">
                    {formatDate(notice.close_date)}
                    <span className="ml-1 text-xs font-normal text-primary-500">
                      (was {formatDate(previousNotice.close_date)})
                    </span>
                  </span>
                ) : notice.close_date ? (
                  formatDate(notice.close_date)
                ) : null
              }
            />
            <NoticesV2.Card.MetadataItem
              label="Award Range"
              value={awardRange}
            />
            <NoticesV2.Card.MetadataItem
              label="Contact"
              value={notice.grantor_contact_name}
            />
            {notice.expected_number_of_awards ? (
              <NoticesV2.Card.MetadataItem
                label="Expected Awards"
                value={`${notice.expected_number_of_awards} award${parseInt(notice.expected_number_of_awards) !== 1 ? 's' : ''}`}
              />
            ) : null}
          </NoticesV2.Card.MetadataGrid>
        )}
      </NoticesV2.Card.BodySection>
    </>
  )
})

// ============================================================================
// Main Component
// ============================================================================

const GrantDisplayV2: React.FC<GrantDisplayV2Props> = ({
  grant,
  similarOpportunities = [],
  isSimilarLoading = false,
  onShare,
}) => {
  const referenceNowMs = useRenderNowMs({ refreshMs: 60_000 })
  const data = useMemo(
    () => normalizeGrantData(grant, referenceNowMs ?? Date.now()),
    [grant, referenceNowMs],
  )
  const { searchId } = useQuerySearchId()
  const { activeOrgId } = useActiveOrgId()
  const { safePush } = useSafePush()
  const {
    mutateAsync: createOpportunityChatSession,
    isPending: isCreatingOpportunityChatSession,
  } = useCreateOpportunityChatSession()

  // Use headless hook for diff computation
  const { diffsByNoticeId, latestDiffs } = useFederalGrantDiffs(data.notices)

  // Derive close date highlight state (subscribe to derived boolean, not raw values)
  const closeDateHighlighted = latestDiffs?.closeDateChanged ?? false

  // Build opportunity input for pursuit actions
  const opportunityInput = useMemo(
    () =>
      buildFederalGrantInput(
        {
          federal_grant_id: data.latestNotice.federal_grant_id,
          opportunity_title: data.title,
          close_date: data.latestNotice.close_date,
        },
        data.aiSummary,
      ),
    [
      data.latestNotice.federal_grant_id,
      data.title,
      data.latestNotice.close_date,
      data.aiSummary,
    ],
  )

  const [activeTab, setActiveTab] = useState('analysis')
  const [documentsTabEntrySource, setDocumentsTabEntrySource] =
    useState<DocumentsTabEntrySource>('default')
  const [selectedDocumentFileId, setSelectedDocumentFileId] = useState<
    string | null
  >(null)
  const [documentFileScrollRequestKey, setDocumentFileScrollRequestKey] =
    useState<number | null>(null)
  const isDocumentsTab = activeTab === 'documents'

  const handleActiveTabChange = useCallback((nextTab: string) => {
    if (nextTab === 'documents') {
      setDocumentsTabEntrySource('default')
    }
    setActiveTab(nextTab)
  }, [])

  // Notice scroll-highlight for timeline click interactions
  const { highlightedNoticeId, scrollToNotice } = useNoticeScrollHighlight()

  // Ref to track pending timeout for cleanup
  const goToNoticeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )
  const goToDocumentsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (goToNoticeTimeoutRef.current) {
        clearTimeout(goToNoticeTimeoutRef.current)
      }
      if (goToDocumentsTimeoutRef.current) {
        clearTimeout(goToDocumentsTimeoutRef.current)
      }
    }
  }, [])

  // Navigate to history tab and scroll to specific notice
  const goToNotice = useCallback(
    (noticeId: string) => {
      setActiveTab('history')
      // Clear any pending timeout
      if (goToNoticeTimeoutRef.current) {
        clearTimeout(goToNoticeTimeoutRef.current)
      }
      // Small delay to let tab switch render
      goToNoticeTimeoutRef.current = setTimeout(
        () => scrollToNotice(noticeId),
        100,
      )
    },
    [scrollToNotice],
  )

  const goToDocumentsTab = useCallback(
    (fileId?: string | null, source: DocumentsTabEntrySource = 'default') => {
      setDocumentsTabEntrySource(source)
      setActiveTab('documents')
      if (fileId) {
        setSelectedDocumentFileId(fileId)
        setDocumentFileScrollRequestKey(Date.now())
      }

      if (goToDocumentsTimeoutRef.current) {
        clearTimeout(goToDocumentsTimeoutRef.current)
      }

      goToDocumentsTimeoutRef.current = setTimeout(() => {
        document
          .getElementById('solicitation-documents-tab-panel')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
    },
    [],
  )

  const analyzeDocumentFile = useCallback(
    async (file: { name: string }) => {
      if (!isUUID(activeOrgId)) return

      const session = await createOpportunityChatSession({
        opportunity_id: data.latestNotice.federal_grant_id,
        opportunity_type: 'federal_grant',
        organization_id: activeOrgId,
        title: `${data.title}: ${cleanFileName(file.name)}`,
      })

      if (session) {
        safePush(`/orgs/${activeOrgId}/c/opp/${session.id}`)
      }
    },
    [
      activeOrgId,
      createOpportunityChatSession,
      data.latestNotice.federal_grant_id,
      data.title,
      safePush,
    ],
  )

  // Memoize amendments list computation for timeline
  const amendments = useMemo(
    () =>
      data.notices
        .slice(0, -1)
        .map((notice, idx) => ({ notice, idx }))
        .filter(({ idx }) => isAmendment(idx, data.notices.length))
        .filter(({ notice }) => notice.post_date)
        .reverse(),
    [data.notices],
  )

  // Use shared hooks - always use latest notice for data display
  const { daysRemaining, isPast: isClosed } = useDaysRemaining(
    data.latestNotice.close_date,
  )
  return (
    <>
      {/* Header Block */}
      <PageTitleV2>
        <PageTitleV2.Badges>
          {data.badges.map((badge) => (
            <BadgeV2
              key={`${badge.variant}-${badge.label}`}
              variant={badge.variant}
            >
              {badge.label}
            </BadgeV2>
          ))}
        </PageTitleV2.Badges>
        <PageTitleV2.Content className="lg:max-w-full">
          <PageTitleV2.Title>{data.title}</PageTitleV2.Title>
        </PageTitleV2.Content>
        <PageTitleV2.Row className="lg:flex-row lg:items-center lg:justify-between lg:gap-4">
          <PageTitleV2.Content>
            <PageTitleV2.Subtitle icon={Building2}>
              {data.subtitle}
            </PageTitleV2.Subtitle>
            {data.opportunityNumber && (
              <PageTitleV2.Meta>{data.opportunityNumber}</PageTitleV2.Meta>
            )}
          </PageTitleV2.Content>
          <PageTitleV2.Actions className="lg:w-auto lg:flex-nowrap">
            <ToggleBookmarkIconButton
              bookmarkableType="federal_grant"
              objectId={grant.id}
              iconSize={16}
              className="h-7 w-7 border border-primary-300 dark:border-primary-700 rounded-md bg-white dark:bg-transparent"
            />
            <Button variant="outline-compact" onClick={onShare}>
              <ShareIcon />
              Share
            </Button>
            {isUUID(activeOrgId) ? (
              <ResourcePermissionGate
                resourceType="organization"
                resourceId={activeOrgId}
                permission="write"
                fallback="hide"
              >
                <PursuitActions.Root
                  opportunity={opportunityInput}
                  searchId={searchId}
                >
                  <PursuitActions.BeginResponseButton className="w-full max-md:h-10 md:w-auto" />
                  <PursuitActions.AskAiButton className="w-full max-md:h-10 md:w-auto" />
                  <PursuitActions.CreatePursuitButton
                    variant="primary-compact"
                    label="Pursue"
                    loadingLabel="Pursuing..."
                    successLabel="Pursuing"
                    className="w-full max-md:h-10 md:w-auto"
                  />
                </PursuitActions.Root>
              </ResourcePermissionGate>
            ) : null}
          </PageTitleV2.Actions>
        </PageTitleV2.Row>
      </PageTitleV2>

      {/* Data Grid */}
      <div className="mb-4 grid grid-cols-2 gap-2 md:mb-6 md:grid-cols-4 md:gap-3">
        <DataBlockV2>
          <DataBlockV2.Header>
            <DataBlockV2.Icon icon={Calendar} color="warning" />
            <DataBlockV2.Label>Application Deadline</DataBlockV2.Label>
          </DataBlockV2.Header>
          <DataBlockV2.Value>{data.stats.closeDate ?? '—'}</DataBlockV2.Value>
          {(isClosed || daysRemaining) && (
            <DataBlockV2.SubValue>
              {isClosed ? 'Closed' : `${daysRemaining} days left`}
            </DataBlockV2.SubValue>
          )}
        </DataBlockV2>
        <DataBlockV2>
          <DataBlockV2.Header>
            <DataBlockV2.Icon icon={Clock} color="accent" />
            <DataBlockV2.Label>Days Remaining</DataBlockV2.Label>
          </DataBlockV2.Header>
          <DataBlockV2.Value>
            {daysRemaining !== null ? (isClosed ? '0' : daysRemaining) : '—'}
          </DataBlockV2.Value>
          <DataBlockV2.SubValue>
            {isClosed ? 'Deadline passed' : 'Until deadline'}
          </DataBlockV2.SubValue>
        </DataBlockV2>
        <DataBlockV2>
          <DataBlockV2.Header>
            <DataBlockV2.Icon icon={DollarSign} color="success" />
            <DataBlockV2.Label>Award Ceiling</DataBlockV2.Label>
          </DataBlockV2.Header>
          <DataBlockV2.Value
            title={formatCurrency(data.funding.awardCeiling) || undefined}
          >
            {formatCurrency(data.funding.awardCeiling) || '—'}
          </DataBlockV2.Value>
        </DataBlockV2>
        <DataBlockV2>
          <DataBlockV2.Header>
            <DataBlockV2.Icon icon={DollarSign} color="info" />
            <DataBlockV2.Label>Total Program Funding</DataBlockV2.Label>
          </DataBlockV2.Header>
          <DataBlockV2.Value
            title={formatCurrency(data.funding.estimatedTotal) || undefined}
          >
            {formatCurrency(data.funding.estimatedTotal) || '—'}
          </DataBlockV2.Value>
        </DataBlockV2>
      </div>

      {/* Main Layout */}
      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-12">
        {/* Main Content Column */}
        <div
          className={twMerge(
            'flex flex-col gap-4',
            isDocumentsTab ? 'lg:col-span-12' : 'lg:col-span-8',
          )}
        >
          {/* Tabs */}
          <TabPanelV2
            value={activeTab}
            onValueChange={handleActiveTabChange}
            defaultTab="analysis"
          >
            <TabPanelV2.List>
              <TabPanelV2.Trigger value="analysis">
                <span className="flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5" />
                  AI Analysis
                </span>
              </TabPanelV2.Trigger>
              <TabPanelV2.Trigger value="history">
                <span className="flex items-center gap-1.5">
                  <ListFilter className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">
                    {data.notices.length === 1
                      ? 'Solicitation Details'
                      : `Solicitation History (${data.notices.length})`}
                  </span>
                  <span className="sm:hidden">
                    {data.notices.length === 1
                      ? 'Details'
                      : `History (${data.notices.length})`}
                  </span>
                </span>
              </TabPanelV2.Trigger>
              <TabPanelV2.Trigger value="documents">
                <span className="flex items-center gap-1.5">
                  <Files className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">
                    Grant Documents ({data.files.length})
                  </span>
                  <span className="sm:hidden">
                    Documents ({data.files.length})
                  </span>
                </span>
              </TabPanelV2.Trigger>
            </TabPanelV2.List>

            {/* Analysis Panel - card container */}
            <TabPanelV2.Content value="analysis">
              <CardV2 variant="accent" className="mt-2">
                <CardV2.Header>
                  <CardV2.HeaderIcon>
                    <Sparkles />
                  </CardV2.HeaderIcon>
                  <CardV2.HeaderTitle>
                    Grant Opportunity Analysis
                  </CardV2.HeaderTitle>
                </CardV2.Header>
                <CardV2.Content>
                  <ContentPanelV2 variant="inline">
                    <ContentPanelV2.Content markdown>
                      {data.aiSummary ||
                        data.description ||
                        'No summary available.'}
                    </ContentPanelV2.Content>
                    <ContentPanelV2.Footer>
                      <ContentPanelV2.CopyButton />
                    </ContentPanelV2.Footer>
                  </ContentPanelV2>
                </CardV2.Content>
              </CardV2>
            </TabPanelV2.Content>

            {/* History Panel - timeline with card-styled notice cards */}
            {/* Reversed order to mirror sidebar timeline (oldest first) */}
            <TabPanelV2.Content value="history" className="mt-2">
              <NoticesV2>
                <NoticesV2.Timeline>
                  {[...data.notices].reverse().map((notice, idx, reversed) => {
                    // Original index in the non-reversed array
                    const originalIdx = data.notices.length - 1 - idx
                    const isAmend = isAmendment(
                      originalIdx,
                      data.notices.length,
                    )
                    const previousNotice = data.notices[originalIdx + 1]
                    const diffs = diffsByNoticeId.get(notice.id)
                    const isCurrentNotice = notice.id === data.latestNotice.id

                    return (
                      <NoticesV2.Card
                        key={notice.id}
                        id={notice.id}
                        isLatest={isCurrentNotice}
                        noticeType={isAmend ? 'amendment' : 'original'}
                        isHighlighted={highlightedNoticeId === notice.id}
                        showConnector
                        isFirst={idx === 0}
                        isLast={idx === reversed.length - 1}
                      >
                        <GrantNoticeCard
                          notice={notice}
                          previousNotice={previousNotice}
                          diffs={diffs}
                          isAmendment={isAmend}
                          isCompact={!isCurrentNotice}
                        />
                      </NoticesV2.Card>
                    )
                  })}
                </NoticesV2.Timeline>
              </NoticesV2>
            </TabPanelV2.Content>

            <TabPanelV2.Content value="documents" className="mt-2">
              <OpportunityDocumentsTab
                files={data.files}
                persistenceKey={data.latestNotice.federal_grant_id}
                selectedFileId={selectedDocumentFileId}
                onSelectedFileIdChange={setSelectedDocumentFileId}
                selectionScrollRequestKey={documentFileScrollRequestKey}
                onSelectionScrollRequestHandled={() =>
                  setDocumentFileScrollRequestKey(null)
                }
                suppressDefaultListOpen={
                  documentsTabEntrySource === 'compact-file-click'
                }
                onAnalyzeFile={analyzeDocumentFile}
                isAnalyzingFile={isCreatingOpportunityChatSession}
                title="Grant Documents"
                emptyTitle="No grant documents"
                emptyMessage="No documents available for this grant."
                searchLabel="Search grant documents"
              />
            </TabPanelV2.Content>
          </TabPanelV2>

          {!isDocumentsTab ? (
            <>
              {/* Divider below tabs */}
              <div className="mt-4 border-t border-primary-200 dark:border-primary-700" />

              {/* Eligibility Section */}
              {data.eligibility.eligibleApplicants && (
                <CardV2 variant="accent" className="mt-3">
                  <CardV2.Header>
                    <CardV2.HeaderIcon>
                      <Users />
                    </CardV2.HeaderIcon>
                    <CardV2.HeaderTitle>Eligible Applicants</CardV2.HeaderTitle>
                  </CardV2.Header>
                  <CardV2.Content>
                    {/* Parse comma-separated applicant types into chips for better readability */}
                    <div className="flex flex-wrap gap-1.5">
                      {data.eligibility.eligibleApplicants
                        .split(/,(?![^()]*\))/)
                        .map((type) => type.trim())
                        .filter(Boolean)
                        .map((applicantType) => (
                          <span
                            key={applicantType}
                            className="inline-flex items-center rounded-md border border-primary-200 bg-primary-50 px-2 py-1 text-xs text-primary-700 dark:border-primary-700 dark:bg-primary-800 dark:text-primary-300"
                          >
                            {applicantType}
                          </span>
                        ))}
                    </div>
                    {data.eligibility.additionalInfo && (
                      <ContentPanelV2
                        size="compact"
                        variant="inline"
                        className="mt-3"
                      >
                        <ContentPanelV2.Header>
                          <span className="text-sm font-semibold uppercase text-primary-400">
                            Additional Eligibility Information
                          </span>
                        </ContentPanelV2.Header>
                        <ContentPanelV2.Content
                          markdown
                          collapsible={4}
                          className="text-xs leading-relaxed"
                        >
                          {data.eligibility.additionalInfo}
                        </ContentPanelV2.Content>
                      </ContentPanelV2>
                    )}
                  </CardV2.Content>
                </CardV2>
              )}

              {/* Documents Section */}
              <OpportunityDocumentsSection
                files={data.files}
                persistenceKey={data.latestNotice.federal_grant_id}
                isHighlighted={false}
                title="Grant Documents"
                emptyTitle="No grant documents"
                emptyMessage="No documents available for this grant."
                searchLabel="Search grant documents"
                className="mt-4"
                onSelectFile={(file) =>
                  goToDocumentsTab(file.id, 'compact-file-click')
                }
              />

              {/* Related Opportunities Section - SEO optimized */}
              <SimilarOpportunitiesV2
                type="federal_grant"
                opportunities={similarOpportunities}
                isLoading={isSimilarLoading}
              >
                <SimilarOpportunitiesV2.Header>
                  Related Grant Opportunities
                </SimilarOpportunitiesV2.Header>
                {isSimilarLoading ? (
                  <SimilarOpportunitiesV2.Skeleton />
                ) : (
                  <SimilarOpportunitiesV2.Grid>
                    {similarOpportunities.map((opp) => (
                      <SimilarOpportunitiesV2.Card
                        key={opp.id}
                        opportunity={opp}
                      />
                    ))}
                  </SimilarOpportunitiesV2.Grid>
                )}
              </SimilarOpportunitiesV2>
            </>
          ) : null}
        </div>

        {/* Sidebar */}
        {!isDocumentsTab ? (
          <div className="space-y-4 lg:col-span-4">
            {/* View Documents Button - mt-3 aligns with tab text (tabs have py-3), hidden on mobile */}
            <ViewDocumentsButton
              fileCount={data.files.length}
              onClick={() => goToDocumentsTab(selectedDocumentFileId)}
              className="mt-3 hidden lg:flex"
            />

            {/* Project Timeline Widget - Color-coded events */}
            <NoticesV2.SidebarTimeline title="Project Timeline">
              {/* Original posting */}
              {data.notices.length > 0 &&
              data.notices[data.notices.length - 1].post_date ? (
                <NoticesV2.TimelineEvent
                  type="posted"
                  icon={CheckCircle2}
                  title="Original Opportunity Posted"
                  date={
                    formatDate(
                      data.notices[data.notices.length - 1].post_date,
                    ) ?? ''
                  }
                  onClick={() =>
                    goToNotice(data.notices[data.notices.length - 1].id)
                  }
                />
              ) : null}
              {/* All amendments - using memoized list */}
              {amendments.map(({ notice, idx }, amendmentIdx) => {
                const isLatestAmendment = idx === 0
                const diffs = diffsByNoticeId.get(notice.id)
                const amendmentNum = amendmentIdx + 1

                return (
                  <NoticesV2.TimelineEvent
                    key={notice.id}
                    type={isLatestAmendment ? 'amended-latest' : 'amended'}
                    icon={Megaphone}
                    title={getAmendmentTitle(
                      isLatestAmendment,
                      amendmentNum,
                      diffs?.descriptionChanged ?? false,
                    )}
                    date={formatDate(notice.post_date) ?? ''}
                    onClick={() => goToNotice(notice.id)}
                  />
                )
              })}
              {/* Deadline - highlight if changed */}
              {data.latestNotice.close_date ? (
                <NoticesV2.TimelineEvent
                  type="deadline"
                  title="Application Deadline"
                  date={formatDate(data.latestNotice.close_date) ?? ''}
                  highlight={closeDateHighlighted}
                />
              ) : null}
              {/* Archive date if available */}
              {data.latestNotice.archive_date ? (
                <NoticesV2.TimelineEvent
                  type="expiry"
                  title="Archive Date"
                  date={formatDate(data.latestNotice.archive_date) ?? ''}
                />
              ) : null}
            </NoticesV2.SidebarTimeline>

            {/* Funding Details Widget */}
            <SidebarWidgetV2>
              <SidebarWidgetV2.Header icon={DollarSign}>
                Funding Details
              </SidebarWidgetV2.Header>
              <SidebarWidgetV2.Content>
                {data.funding.awardCeiling && (
                  <div>
                    <span className="mb-0.5 block text-sm font-semibold uppercase text-primary-400">
                      Award Ceiling
                    </span>
                    <div className="text-sm font-medium text-primary-900 dark:text-primary-100">
                      {formatCurrency(data.funding.awardCeiling)}
                    </div>
                  </div>
                )}
                {data.funding.awardFloor != null &&
                  data.funding.awardFloor > 0 && (
                    <div>
                      <span className="mb-0.5 block text-sm font-semibold uppercase text-primary-400">
                        Award Floor
                      </span>
                      <div className="text-sm font-medium text-primary-900 dark:text-primary-100">
                        {formatCurrency(data.funding.awardFloor)}
                      </div>
                    </div>
                  )}
                {data.funding.estimatedTotal != null &&
                  data.funding.estimatedTotal > 0 && (
                    <div>
                      <span className="mb-0.5 block text-sm font-semibold uppercase text-primary-400">
                        Est. Total Program Funding
                      </span>
                      <div className="text-sm font-medium text-primary-900 dark:text-primary-100">
                        {formatCurrency(data.funding.estimatedTotal)}
                      </div>
                    </div>
                  )}
                {data.funding.expectedAwards &&
                  Number(data.funding.expectedAwards) > 0 && (
                    <div>
                      <span className="mb-0.5 block text-sm font-semibold uppercase text-primary-400">
                        Expected Awards
                      </span>
                      <div className="text-sm font-medium text-primary-900 dark:text-primary-100">
                        {data.funding.expectedAwards}
                      </div>
                    </div>
                  )}
                {/* Cost Sharing */}
                <div className="flex items-center gap-2 pt-1">
                  <CheckCircle2
                    className={`h-4 w-4 ${data.funding.costSharingRequired ? 'text-amber-500' : 'text-emerald-500'}`}
                  />
                  <span className="text-sm text-primary-600 dark:text-primary-400">
                    {data.funding.costSharingRequired
                      ? 'Cost sharing required'
                      : 'No cost sharing required'}
                  </span>
                </div>
              </SidebarWidgetV2.Content>
            </SidebarWidgetV2>

            {/* Agency & Classification Widget */}
            {(data.stats.agencyName ||
              data.stats.fundingCategory ||
              data.stats.fundingType) && (
              <SidebarWidgetV2>
                <SidebarWidgetV2.Header icon={Building2}>
                  Agency & Classification
                </SidebarWidgetV2.Header>
                <SidebarWidgetV2.Content>
                  {data.stats.agencyName && (
                    <div>
                      <span className="mb-0.5 block text-sm font-semibold uppercase text-primary-400">
                        Agency
                      </span>
                      <div className="text-sm font-medium text-primary-900 dark:text-primary-100">
                        {data.stats.agencyName}
                        {data.stats.agencyCode && (
                          <span className="ml-1.5 text-xs font-normal text-primary-500">
                            ({data.stats.agencyCode})
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  {data.stats.fundingCategory && (
                    <div>
                      <span className="mb-0.5 block text-sm font-semibold uppercase text-primary-400">
                        Funding Category
                      </span>
                      <div className="text-sm font-medium text-primary-900 dark:text-primary-100">
                        {data.stats.fundingCategory}
                      </div>
                    </div>
                  )}
                  {data.stats.fundingType && (
                    <div>
                      <span className="mb-0.5 block text-sm font-semibold uppercase text-primary-400">
                        Funding Instrument
                      </span>
                      <div className="text-sm font-medium text-primary-900 dark:text-primary-100">
                        {data.stats.fundingType}
                      </div>
                    </div>
                  )}
                </SidebarWidgetV2.Content>
              </SidebarWidgetV2>
            )}

            {/* Contact Info Widget */}
            {(data.contact.name ||
              data.contact.email ||
              data.contact.phone) && (
              <SidebarWidgetV2>
                <SidebarWidgetV2.Header icon={User}>
                  Grantor Contact
                </SidebarWidgetV2.Header>
                <SidebarWidgetV2.Content>
                  {data.contact.name && (
                    <div>
                      <span className="mb-0.5 block text-sm font-semibold uppercase text-primary-400">
                        Name
                      </span>
                      <div className="flex items-center gap-2">
                        <User className="h-3.5 w-3.5 text-primary-400" />
                        <span className="text-sm font-medium text-primary-900 dark:text-primary-100">
                          {data.contact.name}
                        </span>
                      </div>
                    </div>
                  )}
                  {data.contact.email && (
                    <div>
                      <span className="mb-0.5 block text-sm font-semibold uppercase text-primary-400">
                        Email
                      </span>
                      <a
                        href={`mailto:${data.contact.email}`}
                        className="group flex items-center gap-2 text-sm text-accent-600 hover:text-accent-700 dark:text-accent-400 dark:hover:text-accent-300"
                      >
                        <Mail className="h-3.5 w-3.5" />
                        <span className="truncate">{data.contact.email}</span>
                      </a>
                    </div>
                  )}
                  {data.contact.phone && (
                    <div>
                      <span className="mb-0.5 block text-sm font-semibold uppercase text-primary-400">
                        Phone
                      </span>
                      <a
                        href={`tel:${data.contact.phone}`}
                        className="group flex items-center gap-2 text-sm text-accent-600 hover:text-accent-700 dark:text-accent-400 dark:hover:text-accent-300"
                      >
                        <Phone className="h-3.5 w-3.5" />
                        <span>{data.contact.phone}</span>
                      </a>
                    </div>
                  )}
                </SidebarWidgetV2.Content>
              </SidebarWidgetV2>
            )}

            {/* CFDA Numbers Widget */}
            {data.cfdaNumbers.length > 0 && (
              <SidebarWidgetV2>
                <SidebarWidgetV2.Header icon={Tag}>
                  CFDA Numbers
                </SidebarWidgetV2.Header>
                <SidebarWidgetV2.Content>
                  <div className="flex flex-wrap gap-1.5">
                    {data.cfdaNumbers.map((cfda) => (
                      <span
                        key={cfda}
                        className="rounded border border-primary-200 bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700 dark:border-primary-700 dark:bg-primary-800 dark:text-primary-300"
                      >
                        {cfda}
                      </span>
                    ))}
                  </div>
                </SidebarWidgetV2.Content>
              </SidebarWidgetV2>
            )}

            {/* Official Sources Widget */}
            {data.externalLink && (
              <SidebarWidgetV2>
                <SidebarWidgetV2.Header icon={ExternalLink}>
                  Official Sources
                </SidebarWidgetV2.Header>
                <SidebarWidgetV2.Content>
                  <a
                    href={data.externalLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex w-full items-center justify-center gap-2 rounded-lg border border-primary-200 bg-primary-50 px-4 py-2.5 text-sm font-medium text-primary-700 transition-all hover:border-accent-300 hover:bg-accent-50 hover:text-accent-700 dark:border-primary-700 dark:bg-primary-800 dark:text-primary-300 dark:hover:border-accent-600 dark:hover:bg-accent-900/30 dark:hover:text-accent-400"
                  >
                    View on Grants.gov
                    <ExternalLink className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                  </a>
                </SidebarWidgetV2.Content>
              </SidebarWidgetV2>
            )}
          </div>
        ) : null}
      </div>
    </>
  )
}

GrantDisplayV2.displayName = 'GrantDisplayV2'

export { GrantDisplayV2 }
export type { GrantDisplayV2Props }
